import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  SearchBox,
  Spinner,
  mergeClasses,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { KeyboardEvent, useId, useMemo, useState } from "react";
import { DocumentIcon } from "icons/DocumentIcon";
import { IEngagementSourceDocument } from "apps/project-iq/models/IEngagementSourceDocument";
import {
  formatFileSize,
  getFileTypeInfo,
} from "apps/project-iq/utils/fileUtils";
import {
  ALL_DOCUMENTS_FILTER,
  countDocumentsByTagId,
  filterSourceDocuments,
  getDocumentTypeLabel,
  getTagIdsWithDocuments,
  getPickerFolderLabel,
  groupSourceDocuments,
} from "apps/project-iq/utils/engagementSourceDocumentUtils";
import { useSharedPillButtonStyles } from "theme/sharedStyles";
import {
  getDocumentTypeDotClassName,
  useDocumentTypeTagStyles,
} from "../WorkspaceDocuments/DocumentTypeTag.styles";
import { SourceDocumentTypeBadge } from "../SourceDocumentTypeBadge/SourceDocumentTypeBadge";
import { useSourceDocumentPickerDialogStyles } from "./SourceDocumentPickerDialog.styles";
import { ISourceDocumentPickerDialogProps } from "./SourceDocumentPickerDialog.types";

const SEARCH_PLACEHOLDER = "Search by file name...";
const SEARCH_LABEL = "Search by file name";
const FILTER_GROUP_LABEL = "Filter by document type";
const RADIO_GROUP_LABEL = "Source documents";
const ENGAGEMENT_GROUP_LABEL = "Engagement documents";
const OTHER_GROUP_LABEL = "Other documents";
const METADATA_SEPARATOR = " · ";
const SELECT_AND_IMPORT_LABEL = "Select and import";
const SELECT_DOCUMENT_LABEL = "Select document";
const UPLOAD_LINK_LABEL = "Upload it in Context & Files";
const NO_MATCHING_DOCUMENTS_MESSAGE = "No documents match your search.";
const NO_DOCUMENTS_MESSAGE =
  "No documents in Context & Files yet. Upload one, then come back here.";

export const SourceDocumentPickerDialog = ({
  documents,
  tags,
  isLoading,
  isPlanEmpty,
  initialSelectedFilename = null,
  onConfirm,
  onCancel,
  onUploadRequested,
}: ISourceDocumentPickerDialogProps): JSX.Element => {
  const styles = useSourceDocumentPickerDialogStyles();
  const pillButtonStyles = useSharedPillButtonStyles();
  const documentTypeTagStyles = useDocumentTypeTagStyles();
  const instanceId = useId();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeTagFilter, setActiveTagFilter] =
    useState<string>(ALL_DOCUMENTS_FILTER);
  const [selectedFilename, setSelectedFilename] = useState<string | null>(
    initialSelectedFilename,
  );

  const tagCounts = useMemo(
    () => countDocumentsByTagId(documents),
    [documents],
  );
  const filterTagIds = useMemo(
    () => getTagIdsWithDocuments(tagCounts, tags),
    [tagCounts, tags],
  );

  const visibleDocuments = useMemo(
    () => filterSourceDocuments(documents, searchQuery, activeTagFilter),
    [documents, searchQuery, activeTagFilter],
  );

  const { engagementDocuments, otherDocuments } = useMemo(
    () => groupSourceDocuments(visibleDocuments),
    [visibleDocuments],
  );

  const orderedDocuments = useMemo(
    () => [...engagementDocuments, ...otherDocuments],
    [engagementDocuments, otherDocuments],
  );

  const selectedDocument =
    documents.find(
      (sourceDocument) => sourceDocument.filename === selectedFilename,
    ) ?? null;
  const isSelectedVisible = orderedDocuments.some(
    (sourceDocument) => sourceDocument.filename === selectedFilename,
  );

  const getOptionId = (index: number): string =>
    `${instanceId}-source-document-option-${index}`;

  const handleSelect = (sourceDocument: IEngagementSourceDocument): void => {
    setSelectedFilename(sourceDocument.filename);
  };

  const handleOptionKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number,
  ): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(orderedDocuments[index]);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (index + delta + orderedDocuments.length) % orderedDocuments.length;
      handleSelect(orderedDocuments[nextIndex]);
      document.getElementById(getOptionId(nextIndex))?.focus();
    }
  };

  const handleConfirm = (): void => {
    if (selectedDocument) onConfirm(selectedDocument);
  };

  const renderOption = (
    sourceDocument: IEngagementSourceDocument,
    index: number,
  ): JSX.Element => {
    const isSelected = sourceDocument.filename === selectedFilename;
    const isTabStop = isSelectedVisible ? isSelected : index === 0;
    const typeInfo = getFileTypeInfo(sourceDocument.originalFilename);
    const documentTypeLabel = getDocumentTypeLabel(
      sourceDocument.documentType,
      tags,
    );
    const folderLabel = getPickerFolderLabel(sourceDocument.filename);
    const sizeLabel =
      typeof sourceDocument.sizeBytes === "number"
        ? formatFileSize(sourceDocument.sizeBytes)
        : null;
    const metaText = [typeInfo.label, sizeLabel, folderLabel]
      .filter(Boolean)
      .join(METADATA_SEPARATOR);
    const iconClassName =
      typeInfo.style === "pdf"
        ? styles.fileIconPdf
        : typeInfo.style === "xls"
          ? styles.fileIconXls
          : typeInfo.style === "ppt"
            ? styles.fileIconPpt
            : styles.fileIconDefault;

    return (
      <div
        key={sourceDocument.filename}
        id={getOptionId(index)}
        role="radio"
        aria-checked={isSelected}
        aria-label={
          documentTypeLabel
            ? `${sourceDocument.originalFilename}, ${documentTypeLabel}`
            : sourceDocument.originalFilename
        }
        tabIndex={isTabStop ? 0 : -1}
        className={mergeClasses(
          styles.option,
          isSelected && styles.optionSelected,
        )}
        onClick={() => handleSelect(sourceDocument)}
        onKeyDown={(event) => handleOptionKeyDown(event, index)}
      >
        <span
          className={mergeClasses(
            styles.radioDot,
            isSelected && styles.radioDotSelected,
          )}
          aria-hidden="true"
        >
          {isSelected && <span className={styles.radioDotInner} />}
        </span>
        <span
          className={mergeClasses(styles.fileIconWrapper, iconClassName)}
          aria-hidden="true"
        >
          <DocumentIcon size={18} />
        </span>
        <span className={styles.optionInfo}>
          <span className={styles.optionName}>
            {sourceDocument.originalFilename}
          </span>
          {metaText && <span className={styles.optionMeta}>{metaText}</span>}
        </span>
        {sourceDocument.documentType && documentTypeLabel && (
          <SourceDocumentTypeBadge
            documentType={sourceDocument.documentType}
            label={documentTypeLabel}
          />
        )}
      </div>
    );
  };

  const renderGroup = (
    groupLabel: string,
    groupDocuments: IEngagementSourceDocument[],
    startIndex: number,
  ): JSX.Element | null => {
    if (groupDocuments.length === 0) return null;
    return (
      <div role="presentation" className={styles.groupSection}>
        <div role="presentation" className={styles.groupHeading}>
          {groupLabel}
        </div>
        {groupDocuments.map((sourceDocument, groupIndex) =>
          renderOption(sourceDocument, startIndex + groupIndex),
        )}
      </div>
    );
  };

  const renderList = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Spinner label="Loading documents..." size="small" />
        </div>
      );
    }
    if (documents.length === 0) {
      return <div className={styles.emptyState}>{NO_DOCUMENTS_MESSAGE}</div>;
    }
    if (orderedDocuments.length === 0) {
      return (
        <div className={styles.emptyState}>{NO_MATCHING_DOCUMENTS_MESSAGE}</div>
      );
    }
    return (
      <div
        className={styles.list}
        role="radiogroup"
        aria-label={RADIO_GROUP_LABEL}
      >
        {renderGroup(ENGAGEMENT_GROUP_LABEL, engagementDocuments, 0)}
        {renderGroup(
          OTHER_GROUP_LABEL,
          otherDocuments,
          engagementDocuments.length,
        )}
      </div>
    );
  };

  const renderFilterChip = (
    tagId: string | null,
    label: string,
    count: number,
  ): JSX.Element => {
    const filterValue = tagId ?? ALL_DOCUMENTS_FILTER;
    const isActive = activeTagFilter === filterValue;
    return (
      <button
        key={filterValue}
        type="button"
        className={mergeClasses(
          pillButtonStyles.pillButton,
          styles.filterChipCompact,
          isActive && pillButtonStyles.pillButtonActive,
        )}
        aria-pressed={isActive}
        onClick={() => setActiveTagFilter(filterValue)}
      >
        <span className={styles.filterChipLabel}>
          {tagId && (
            <span
              className={mergeClasses(
                documentTypeTagStyles.dot,
                getDocumentTypeDotClassName(documentTypeTagStyles, tagId),
              )}
              aria-hidden="true"
            />
          )}
          {label}
          <span className={styles.countText}>{count}</span>
        </span>
      </button>
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(_event, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            className={styles.title}
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onCancel}
              />
            }
          >
            Choose a source document
          </DialogTitle>
          <DialogContent>
            <div className={styles.content}>
              <p className={styles.subtitle}>
                Milestones, deliverables and RAID log entries will be drafted
                from the document you choose.
              </p>
              <SearchBox
                autoFocus
                className={styles.searchBox}
                placeholder={SEARCH_PLACEHOLDER}
                aria-label={SEARCH_LABEL}
                value={searchQuery}
                onChange={(_event, data) => setSearchQuery(data.value)}
              />
              <div
                className={styles.filterRow}
                role="group"
                aria-label={FILTER_GROUP_LABEL}
              >
                {renderFilterChip(null, "All", documents.length)}
                {filterTagIds.map((tagId) =>
                  renderFilterChip(
                    tagId,
                    getDocumentTypeLabel(tagId, tags) ?? tagId,
                    tagCounts[tagId] ?? 0,
                  ),
                )}
              </div>
              {renderList()}
            </div>
          </DialogContent>
          <DialogActions fluid className={styles.footer}>
            <span className={styles.footerHint}>
              {"Can't find it? "}
              <Link
                as="button"
                className={styles.footerLink}
                onClick={onUploadRequested}
              >
                {UPLOAD_LINK_LABEL}
              </Link>
            </span>
            <span className={styles.footerActions}>
              <Button appearance="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={!selectedDocument}
                onClick={handleConfirm}
              >
                {isPlanEmpty ? SELECT_AND_IMPORT_LABEL : SELECT_DOCUMENT_LABEL}
              </Button>
            </span>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
